/**
 * Equalizer - анимированные полоски эквалайзера (Instagram-style)
 *
 * Высота полосок зависит от реального уровня громкости (audioLevel)
 * audioLevel: число от 0 до 1, где 0 = тишина, 1 = максимальная громкость
 *
 * Анимация:
 * 1. Сначала нет баров
 * 2. При голосе бары появляются слева направо
 * 3. Когда заполнена вся ширина, бары сдвигаются влево (как в Instagram)
 */

import { memo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './Equalizer.css';

// Animation constants
const UPDATE_INTERVAL = 50; // Update every 50ms for smooth animation
const AUDIO_THRESHOLD = 0.01; // Threshold for detecting speech
const MIN_BAR_HEIGHT = 2; // Minimum visible height in pixels
const MAX_BAR_HEIGHT_RANGE = 38; // Range from min to max (2px to 40px) - увеличен контраст

export const Equalizer = memo(({
  isSpeaking = false,
  isUserSpeaking = false,
  userAudioLevel = 0,
  agentAudioLevel = 0,
  barCount = 50,
  primaryColor,
  theme
}) => {
  // Determine if active (either agent or user is speaking)
  const isActive = isSpeaking || isUserSpeaking;

  // Use the appropriate audio level based on who is speaking
  const audioLevel = isUserSpeaking ? userAudioLevel : agentAudioLevel;

  // Use CSS variable from theme if no explicit color provided
  const barColor = primaryColor || 'var(--widget-primary, #2142FF)';

  // History of audio levels (for Instagram-style scrolling animation)
  const [audioHistory, setAudioHistory] = useState([]);
  const lastUpdateRef = useRef(0);
  const wasActiveRef = useRef(false);
  const rafIdRef = useRef(null); // FIXED: Use ref to store RAF id

  // Add new audio level to history
  useEffect(() => {
    // Track speaking state transitions
    const justStartedSpeaking = isActive && !wasActiveRef.current;
    wasActiveRef.current = isActive;

    // When just started speaking: boost existing bars as visual feedback
    if (justStartedSpeaking) {
      setAudioHistory(prev => {
        if (prev.length === 0) return prev;
        // Boost all bars from left to right as "we hear you" signal
        return prev.map((level, i) => {
          const position = i / prev.length;
          const boostFactor = 1.5 * (1 - position * 0.5); // Stronger boost on the left
          return Math.min(1, level * boostFactor);
        });
      });
    }

    // Continue animation only if was active at least once
    if (!isActive && audioHistory.length === 0) return;

    const updateHistory = () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < UPDATE_INTERVAL) return;

      lastUpdateRef.current = now;

      setAudioHistory(prev => {
        if (isActive && audioLevel > AUDIO_THRESHOLD) {
          // Active and speaking: add new bar with current audio level
          const normalizedLevel = audioLevel;

          // If array is full, shift right (remove last, add first) - REVERSE direction
          if (prev.length >= barCount) {
            return [normalizedLevel, ...prev.slice(0, -1)];
          }

          // Otherwise, add to the beginning (filling from left)
          return [normalizedLevel, ...prev];
        } else {
          // Pause: keep existing bars as is (don't add new ones)
          return prev;
        }
      });
    };

    // Use requestAnimationFrame for smooth updates
    const loop = () => {
      updateHistory();
      rafIdRef.current = requestAnimationFrame(loop);
    };

    rafIdRef.current = requestAnimationFrame(loop);

    // FIXED: Cleanup properly using ref
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isActive, audioLevel, barCount]); // FIXED: Removed audioHistory.length to prevent infinite loop

  return (
    <div className="equalizer">
      {audioHistory.map((level, i) => {
        // Calculate height based on audio level (4px to 32px)
        const targetHeight = MIN_BAR_HEIGHT + (level * MAX_BAR_HEIGHT_RANGE);

        return (
          <div
            key={`${i}-${audioHistory.length}`}
            className="equalizer__bar equalizer__bar--active"
            style={{
              '--bar-color': barColor,
              height: `${targetHeight}px`,
            }}
          />
        );
      })}
    </div>
  );
});

Equalizer.displayName = 'Equalizer';

Equalizer.propTypes = {
  isSpeaking: PropTypes.bool,
  isUserSpeaking: PropTypes.bool,
  userAudioLevel: PropTypes.number,
  agentAudioLevel: PropTypes.number,
  barCount: PropTypes.number,
  primaryColor: PropTypes.string,
  theme: PropTypes.shape({
    variant: PropTypes.oneOf(['light', 'dark']),
    primaryColor: PropTypes.string,
    accentColor: PropTypes.string,
    colors: PropTypes.object,
    sizing: PropTypes.object,
    spacing: PropTypes.object,
    radius: PropTypes.object,
  }),
};
